import {computed, type ComputedRef, type Ref} from "vue"
import {forgetCasualGames, useCasualGames} from "@/domains/games"
import type {GameCode, Game} from "../adapters/esports"
import {sizeOf, srcsetOf} from "@/components/island/pictures"
import {BRAND_ACCENT} from "@/utils/brand"

/** A game as the island draws it: its name, its colour, and the art it carries. */
export interface GameIdentity {
  name: string
  /** The accent that carries this game across the island. */
  accent: string
  /** The game's own icon, where one has been chosen for it. */
  icon: string | null
  /** The widths that icon is stored at, ready for a `srcset`. */
  iconSrcset?: string
  /** The picture in the game's slice on the index, where one has been chosen for it. */
  banner: string | null
  /** The widths that picture is stored at, ready for a `srcset`. */
  srcset?: string
  /** Its own dimensions, so the browser reserves its space before the bytes arrive. */
  width?: number
  height?: number
  /** The names of the Discord channels its esports players meet in. */
  channels?: string[]
}

/**
 * A game nobody has drawn art for reads on the association's own blue and shows no icon. Its
 * name still comes from its record; there is no game without one.
 */
const UNDRAWN = {accent: BRAND_ACCENT, icon: null, banner: null}

const identify = (record: Game): GameIdentity => ({
  name: record.name,
  accent: record.accent || UNDRAWN.accent,
  icon: record.icon?.url ?? null,
  iconSrcset: srcsetOf(record.icon),
  banner: record.banner?.url ?? null,
  srcset: srcsetOf(record.banner),
  ...sizeOf(record.banner),
  channels: (record.esportsChannels ?? []).map(one => one.name),
})

/**
 * The games the association knows, read once and shared.
 *
 * Which games exist, what each is called and the art each carries are one answer from the api,
 * so a page and the database cannot disagree. The casual pages read the same answer, so this
 * shares their list rather than asking the api a second time.
 */
export function useGames(): {
  games: Ref<Game[]>
  current: ComputedRef<Game[]>
  ready: Promise<Game[]>
  identityOf: (game: GameCode | string) => GameIdentity
  recordOf: (game: GameCode | string) => Game | null
  bySlug: (slug: string) => Game | null
  refresh: () => Promise<Game[]>
} {
  const {games: records, ready, refresh} = useCasualGames()
  const recordOf = (game: GameCode | string) => records.value.find(one => one.code === game) ?? null

  return {
    games: records,
    current: computed(() => records.value.filter(one => one.inCompetition)),
    ready,
    identityOf: (game) => {
      const record = recordOf(game)
      // No record means the records have not answered yet, or the code names no game. Either
      // way there is no name to print: the raw code is not one, and showing it flashes.
      return record ? identify(record) : {name: "", ...UNDRAWN}
    },
    recordOf,
    bySlug: (slug) => records.value.find(one => one.slug === slug) ?? null,
    refresh,
  }
}

/** Forgets what was read, so a test can start from nothing. */
export const forgetGames = forgetCasualGames
